/**
 * Serves `@divespots/contracts/fixtures` through the same client functions as the live API.
 * Mimics the public API rules from contracts/routes.ts: anonymous callers only ever see
 * `status: "published"` spots, and only published objects inside them.
 */
import type {
  DiveObject,
  ObjectModel,
  Role,
  SpotDetail,
  SpotListItem,
  Terrain,
} from "@divespots/contracts";
import {
  AdminUser,
  MODEL_CONTENT_TYPE,
  MODEL_MAX_BYTES,
  TERRAIN_CONTENT_TYPE,
  encodeTerrain,
} from "@divespots/contracts";
import {
  diveLakesFixture,
  diveManifestFixture,
  lakesFixture,
  makeDiveOverviewFixture,
  makeDiveTileFixture,
  makeTerrainFixture,
  makePrecipFieldFixture,
  makeWindFieldFixture,
  spotDetailsFixture,
} from "@divespots/contracts/fixtures";
import { auditFixtureList } from "./audit-fixtures.ts";
import { glbSummary } from "../admin/models/glb.ts";
import { getFixtureDelayMs } from "./config.ts";
import type { Transport, TransportRequest, TransportResponse } from "./transport.ts";

/** Round-trip through JSON so the client sees exactly what the wire would carry. */
const toWire = <T>(v: T): unknown => JSON.parse(JSON.stringify(v));

const errorBody = (code: string, message: string) => ({ error: { code, message } });

export function publicSpotDetails(): SpotDetail[] {
  return spotDetailsFixture
    .filter((s) => s.status === "published")
    .map((s) => {
      const objects = s.objects.filter((o) => o.status === "published");
      return { ...s, objects, objectCount: objects.length };
    });
}

function toListItem(s: SpotDetail): SpotListItem {
  return {
    id: s.id,
    slug: s.slug,
    name: s.name,
    location: s.location,
    lakeSlug: s.lakeSlug,
    hasBathy: s.hasBathy,
    waterBody: s.waterBody,
    locality: s.locality,
    difficulty: s.difficulty,
    maxDepthM: s.maxDepthM,
    closedPeriods: s.closedPeriods,
    permanentClosure: s.permanentClosure,
    objectCount: s.objectCount,
    status: s.status,
  };
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
}

/**
 * E2E builds only: `localStorage["divespots.e2e.role"] = "moderator"` signs the browser in as staff
 * (fixtures mode has no login) and lets object create/move succeed in memory, so the staff flows
 * can be tested without the live API. Never active in a normal build.
 */
function e2eRole(): Role | null {
  if (import.meta.env?.VITE_E2E !== "1" || typeof localStorage === "undefined") return null;
  try {
    const r = localStorage.getItem("divespots.e2e.role");
    return r === "admin" || r === "moderator" || r === "diver" ? r : null;
  } catch {
    return null;
  }
}
/** E2E builds only: `localStorage["divespots.e2e.dive"] = "1"` grants dive mode to a non-admin sign-in. */
function e2eDive(): boolean {
  const role = e2eRole();
  if (role === null) return false;
  if (role === "admin") return true;
  try {
    return localStorage.getItem("divespots.e2e.dive") === "1";
  } catch {
    return false;
  }
}

/** The shared gate of every dive route: 401 signed out, 403 without access, else null. */
function diveDenied(): TransportResponse | null {
  if (e2eRole() === null) {
    return { status: 401, json: errorBody("unauthorized", "Authentication required") };
  }
  return e2eDive() ? null : { status: 403, json: errorBody("forbidden", "dive access required") };
}

/** Lake, version and the terrain binary of a dive tile/overview request (404 / 409 like the API). */
function diveBinary(req: TransportRequest, terrain: () => Terrain | null): TransportResponse {
  const denied = diveDenied();
  if (denied) return denied;
  if (req.params.slug !== diveManifestFixture.lakeSlug) {
    return { status: 404, json: errorBody("not_found", "No dive data for this lake") };
  }
  if (req.query.v !== undefined && req.query.v !== diveManifestFixture.version) {
    return { status: 409, json: errorBody("conflict", "dive set rebuilt") };
  }
  const t = terrain();
  if (!t) return { status: 404, json: errorBody("not_found", "No lake-floor data here") };
  return { status: 200, binary: encodeTerrain(t), contentType: TERRAIN_CONTENT_TYPE };
}

/** In-memory models (E2E and dev only): validates the size, nothing else; the real API does the rest. */
const e2eModels = new Map<string, { model: ObjectModel; bytes: ArrayBuffer }>();

/** A stable 64-hex stand-in for the SHA-256 (the fixtures never need the real one). */
function fakeSha(bytes: ArrayBuffer): string {
  const u = new Uint8Array(bytes);
  let out = "";
  for (let k = 0; k < 8; k++) {
    let h = 0x811c9dc5 ^ k;
    for (let i = 0; i < u.length; i++) h = Math.imul(h ^ (u[i] as number), 0x01000193) >>> 0;
    out += h.toString(16).padStart(8, "0");
  }
  return out;
}

const knownObject = (id: string) =>
  e2eObjects.get(id) ?? spotDetailsFixture.flatMap((s) => s.objects).find((o) => o.id === id);

const e2eObjects = new Map<string, DiveObject>();
const e2eMoved = new Map<string, DiveObject["location"]>();

/** E2E only: the admin user list (invented people on reserved domains only; one very long address on purpose, to test the table layout). */
const e2eUsers = [
  {
    id: "e2e-user",
    email: "e2e@divespots.test",
    name: "E2E",
    role: "admin",
    createdAt: "2026-10-31T08:00:00.000Z",
    lastSeenAt: "2026-10-31T09:00:00.000Z",
    diveAccess: true,
  },
  {
    id: "e2e-u2",
    email: "averyveryverylong.firstname.lastname@example-diving-club.test",
    name: "Anna Muster-Beispiel",
    role: "diver",
    createdAt: "2026-09-02T08:00:00.000Z",
    lastSeenAt: null,
    pendingSuggestions: 3,
  },
  {
    id: "e2e-u3",
    email: "max.beispiel@example.com",
    name: "Max Beispiel",
    role: "moderator",
    createdAt: "2026-08-14T08:00:00.000Z",
    lastSeenAt: "2026-10-30T18:00:00.000Z",
    diveAccess: true,
  },
  {
    id: "e2e-u4",
    email: "mia.keller@example.com",
    name: "Mia Keller",
    role: "diver",
    banned: true,
    createdAt: "2026-07-01T08:00:00.000Z",
    lastSeenAt: "2026-07-20T08:00:00.000Z",
  },
].map((u) => AdminUser.parse({ banned: false, pendingSuggestions: 0, diveAccess: false, ...u }));

/** The fixtures API without latency; the offline e2e serves it over HTTP. */
export function handle(req: TransportRequest): TransportResponse {
  switch (req.route) {
    case "health":
      return { status: 200, json: { ok: true } };
    case "me": {
      const role = e2eRole();
      if (role) {
        return {
          status: 200,
          json: { user: { id: "e2e-user", email: "e2e@divespots.test", name: "E2E", role } },
        };
      }
      return { status: 401, json: errorBody("unauthorized", "Authentication required") };
    }
    case "meFeatures": {
      if (e2eRole() === null) {
        return { status: 401, json: errorBody("unauthorized", "Authentication required") };
      }
      return { status: 200, json: { dive: e2eDive() } };
    }
    case "listDiveLakes":
      return diveDenied() ?? { status: 200, json: toWire(diveLakesFixture) };
    case "diveManifest": {
      const denied = diveDenied();
      if (denied) return denied;
      if (req.params.slug !== diveManifestFixture.lakeSlug) {
        return { status: 404, json: errorBody("not_found", "No dive data for this lake") };
      }
      return { status: 200, json: toWire(diveManifestFixture) };
    }
    case "diveTile": {
      const x = Number(req.params.x);
      const y = Number(req.params.y);
      return diveBinary(req, () =>
        Number.isInteger(x) && Number.isInteger(y) ? makeDiveTileFixture(x, y) : null,
      );
    }
    case "diveOverview":
      return diveBinary(req, makeDiveOverviewFixture);
    case "lakeOverview": {
      // public, like the live route; only the Thunersee fixture has an overview (the dive fixture's)
      const lake = lakesFixture.find((l) => l.slug === req.params.slug);
      if (!lake || lake.slug !== diveManifestFixture.lakeSlug) {
        return { status: 404, json: errorBody("not_found", "No overview for this lake") };
      }
      const t = makeDiveOverviewFixture();
      const withLevel: Terrain = { ...t, meta: { ...t.meta, waterLevelM: lake.waterLevelM } };
      return {
        status: 200,
        binary: encodeTerrain(withLevel),
        contentType: TERRAIN_CONTENT_TYPE,
      };
    }
    case "listObjectModels": {
      const staff = e2eRole() === "moderator" || e2eRole() === "admin";
      const wanted = spotDetailsFixture.filter((s) =>
        req.query.spotId ? s.id === req.query.spotId : s.lakeSlug === req.query.lake,
      );
      const ids = new Set(
        wanted.flatMap((s) => [
          ...s.objects.map((o) => o.id),
          ...[...e2eObjects.values()].filter((o) => o.spotId === s.id).map((o) => o.id),
        ]),
      );
      const models = [...e2eModels.values()]
        .filter(
          ({ model }) =>
            ids.has(model.objectId) &&
            (staff || knownObject(model.objectId)?.status === "published"),
        )
        .map(({ model }) => model);
      return { status: 200, json: toWire({ models }) };
    }
    case "getObjectModelFile": {
      const sha = (req.params.file ?? "").replace(/\.glb$/, "");
      const staff = e2eRole() === "moderator" || e2eRole() === "admin";
      const hit = [...e2eModels.values()].find(
        ({ model }) =>
          model.sha256 === sha && (staff || knownObject(model.objectId)?.status === "published"),
      );
      if (!hit) return { status: 404, json: errorBody("not_found", "Model not found") };
      return { status: 200, binary: hit.bytes, contentType: MODEL_CONTENT_TYPE };
    }
    case "putObjectModel":
    case "updateObjectModel":
    case "deleteObjectModel": {
      const role = e2eRole();
      if (role === null) {
        return { status: 401, json: errorBody("unauthorized", "Authentication required") };
      }
      if (role !== "admin") return { status: 403, json: errorBody("forbidden", "admin only") };
      const id = req.params.id as string;
      if (!knownObject(id))
        return { status: 404, json: errorBody("not_found", "Object not found") };
      const current = e2eModels.get(id);
      if (req.route === "deleteObjectModel") {
        if (!current) return { status: 404, json: errorBody("not_found", "No model") };
        e2eModels.delete(id);
        return { status: 204 };
      }
      const now = new Date().toISOString();
      if (req.route === "updateObjectModel") {
        if (!current) return { status: 404, json: errorBody("not_found", "No model") };
        const patch = req.body as Partial<
          Pick<ObjectModel, "scale" | "headingDeg" | "attribution">
        >;
        const model = { ...current.model, ...patch, updatedAt: now };
        e2eModels.set(id, { ...current, model });
        return { status: 200, json: toWire(model) };
      }
      const bytes = req.rawBody;
      if (!(bytes instanceof ArrayBuffer)) {
        return {
          status: 400,
          json: errorBody("bad_request", "the fixtures API needs an ArrayBuffer body"),
        };
      }
      if (bytes.byteLength > MODEL_MAX_BYTES) {
        return { status: 413, json: errorBody("payload_too_large", "model file too large") };
      }
      const attribution = (req.query.attribution ?? "").trim();
      const scale = Number(req.query.scale ?? 1);
      const headingDeg = Number(req.query.headingDeg ?? 0);
      if (
        !attribution ||
        !(scale >= 0.01 && scale <= 100) ||
        !(headingDeg >= 0 && headingDeg < 360) ||
        new DataView(bytes).byteLength < 12 ||
        new DataView(bytes).getUint32(0, true) !== 0x46546c67
      ) {
        return { status: 400, json: errorBody("bad_request", "invalid model upload") };
      }
      const sha256 = fakeSha(bytes);
      const model: ObjectModel = {
        objectId: id,
        sha256,
        url: `/api/object-models/${sha256}.glb`,
        sizeBytes: Math.max(1, bytes.byteLength),
        ...glbSummary(bytes),
        scale,
        headingDeg,
        attribution,
        uploadedAt: current?.model.uploadedAt ?? now,
        updatedAt: now,
      };
      e2eModels.set(id, { model, bytes });
      return { status: 200, json: toWire(model) };
    }
    case "listLakes":
      return { status: 200, json: toWire({ lakes: lakesFixture }) };
    case "precip":
      return { status: 200, json: toWire(makePrecipFieldFixture(Date.now())) };
    case "wind":
      return { status: 200, json: toWire(makeWindFieldFixture(Date.now())) };
    case "listSpots": {
      let spots = publicSpotDetails().map(toListItem);
      const lake = req.query.lake;
      if (lake) spots = spots.filter((s) => s.lakeSlug === lake);
      const bbox = req.query.bbox?.split(",").map(Number);
      if (bbox && bbox.length === 4 && bbox.every(Number.isFinite)) {
        const [w, s, e, n] = bbox as [number, number, number, number];
        spots = spots.filter(
          (p) =>
            p.location.lon >= w &&
            p.location.lon <= e &&
            p.location.lat >= s &&
            p.location.lat <= n,
        );
      }
      return { status: 200, json: toWire({ spots }) };
    }
    case "getSpot": {
      const found = publicSpotDetails().find((s) => s.slug === req.params.slug);
      if (!found) return { status: 404, json: errorBody("not_found", "Spot not found") };
      if (e2eRole() === null) return { status: 200, json: toWire(found) };
      const objects = [
        ...found.objects,
        ...[...e2eObjects.values()].filter((o) => o.spotId === found.id),
      ].map((o) =>
        e2eMoved.has(o.id) ? { ...o, location: e2eMoved.get(o.id) as DiveObject["location"] } : o,
      );
      return { status: 200, json: toWire({ ...found, objects, objectCount: objects.length }) };
    }
    case "listObjects": {
      if (e2eRole() === null) {
        return { status: 401, json: errorBody("unauthorized", "Authentication required") };
      }
      const spot = spotDetailsFixture.find((x) => x.id === req.query.spotId);
      const objects = [
        ...(spot?.objects ?? []),
        ...[...e2eObjects.values()].filter((o) => o.spotId === req.query.spotId),
      ];
      return { status: 200, json: toWire({ objects }) };
    }
    case "createObject": {
      if (e2eRole() !== "moderator" && e2eRole() !== "admin") {
        return { status: 401, json: errorBody("unauthorized", "Authentication required") };
      }
      const b = req.body as Record<string, unknown> & { depthOverrideM?: number | null };
      const override = b.depthOverrideM ?? null;
      const now = new Date().toISOString();
      const o = {
        description: {},
        depthOverrideM: null,
        status: "draft",
        ...b,
        id: crypto.randomUUID(),
        depthComputedM: null,
        depth: { valueM: override, source: override === null ? null : "override" },
        lastSighting: null,
        sightingCount: 0,
        createdAt: now,
        updatedAt: now,
      } as unknown as DiveObject;
      e2eObjects.set(o.id, o);
      return { status: 201, json: toWire(o) };
    }
    case "updateObject": {
      const id = req.params.id as string;
      const known =
        e2eObjects.get(id) ?? spotDetailsFixture.flatMap((s) => s.objects).find((o) => o.id === id);
      if (!known) return { status: 404, json: errorBody("not_found", "Object not found") };
      const patch = req.body as { location?: DiveObject["location"] };
      if (patch.location) e2eMoved.set(id, patch.location);
      return {
        status: 200,
        json: toWire({ ...known, ...(patch.location ? { location: patch.location } : {}) }),
      };
    }
    case "listUsers": {
      if (e2eRole() !== "admin") {
        return { status: 403, json: errorBody("forbidden", "admin only") };
      }
      const q = String(req.query.q ?? "").toLowerCase();
      const users = e2eUsers.filter(
        (u) =>
          (!q || `${u.name} ${u.email}`.toLowerCase().includes(q)) &&
          (!req.query.role || u.role === req.query.role) &&
          (req.query.banned === undefined || String(u.banned) === String(req.query.banned)),
      );
      return { status: 200, json: toWire({ users }) };
    }
    case "listAudit": {
      if (e2eRole() !== "admin") {
        return { status: 403, json: errorBody("forbidden", "admin only") };
      }
      const list = auditFixtureList(req.query);
      return list
        ? { status: 200, json: toWire(list) }
        : { status: 400, json: errorBody("bad_request", "invalid cursor") };
    }
    case "terrain": {
      // Served as real bytes through encodeTerrain, so the client decodes it exactly as in live mode.
      const lat = Number(req.query.lat);
      const lon = Number(req.query.lon);
      const radius = Number(req.query.radius ?? 150);
      if (
        ![lat, lon, radius].every(Number.isFinite) ||
        !Number.isInteger(radius) ||
        radius < 20 ||
        radius > 300
      ) {
        return { status: 400, json: errorBody("bad_request", "invalid terrain query") };
      }
      const fixture = makeTerrainFixture(radius);
      const dLat = (lat - fixture.meta.center.lat) * 111_000;
      const dLon = (lon - fixture.meta.center.lon) * 76_000;
      if (Math.hypot(dLat, dLon) > 150) {
        return { status: 404, json: errorBody("not_found", "No lake-floor data here") };
      }
      return { status: 200, binary: encodeTerrain(fixture), contentType: TERRAIN_CONTENT_TYPE };
    }
    default:
      return {
        status: 501,
        json: errorBody("internal", `${req.route} is not available in fixtures mode`),
      };
  }
}

export const fixturesTransport: Transport = async (req) => {
  const delay = getFixtureDelayMs();
  if (delay > 0) await sleep(delay, req.signal);
  return handle(req);
};
